/**
 * Pattern matching for GG/GMA type & method filters.
 *
 * Rules (shared by both filters):
 * - Comma separates patterns, whitespace around each pattern is trimmed.
 * - `*` matches any sequence, `?` matches a single character.
 * - Matching is case-insensitive.
 * - OR logic across patterns: matching any pattern includes the item.
 * - Invalid patterns are treated as non-matching (safe fallback).
 */

/** Compile a single wildcard pattern into a case-insensitive RegExp, or null when invalid/empty. */
function patternToRegExp(pattern: string): RegExp | null {
  const trimmed = pattern.trim()
  if (!trimmed) return null

  try {
    const escaped = trimmed.replace(/[.+^${}()|[\]\\]/g, '\\$&')
    const wildcarded = escaped.replaceAll('*', '.*').replaceAll('?', '.')
    return new RegExp(`^${wildcarded}$`, 'i')
  } catch {
    return null
  }
}

/** Split a raw comma-separated filter string into trimmed, non-empty patterns. */
export function parseFilterPatterns(value: string): string[] {
  return value
    .split(',')
    .map((pattern) => pattern.trim())
    .filter(Boolean)
}

/** Whether a value matches a single wildcard pattern (case-insensitive). */
export function matchesPattern(value: string, pattern: string): boolean {
  const regex = patternToRegExp(pattern)
  return regex ? regex.test(value) : false
}

/** Whether a value matches any of the provided patterns (OR logic). */
export function matchesAnyPattern(value: string, patterns: string[]): boolean {
  return patterns.some((pattern) => matchesPattern(value, pattern))
}
